import { z } from "zod";
import type { ListQuery, SortOrder } from "@/api/types";

/**
 * URL state for list pages: search, sort, pagination and filters all live in query params so
 * views survive reload and can be shared (e.g. /organizations?status=ACTIVE&sort=createdAt&order=desc&page=2).
 */
export const baseListSearch = {
  q: z.string().trim().min(1).max(120).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  pageSize: z.coerce
    .number()
    .int()
    .refine((n) => [25, 50, 100].includes(n))
    .optional()
    .catch(undefined),
  sort: z.string().max(40).optional().catch(undefined),
  order: z.enum(["asc", "desc"]).optional().catch(undefined),
};

export const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values).optional().catch(undefined);
export const optionalString = z.string().trim().min(1).max(120).optional().catch(undefined);
export const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
export const optionalInt = z.coerce.number().int().min(0).optional().catch(undefined);

export interface BaseListSearch {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: SortOrder;
}

export function toListQuery<F extends object>(
  search: BaseListSearch & F,
  filterKeys: ReadonlyArray<keyof F>,
  defaults: { sort: string; order?: SortOrder; pageSize?: number },
): ListQuery<F> {
  const filters = {} as F;
  for (const k of filterKeys) if (search[k] !== undefined) filters[k] = search[k];
  return {
    q: search.q,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? defaults.pageSize ?? 50,
    sort: search.sort ?? defaults.sort,
    order: search.order ?? defaults.order ?? "desc",
    filters,
  };
}

/** Changing filters or search resets to page 1. */
export function withFilterPatch<S extends BaseListSearch>(prev: S, patch: Partial<S>): S {
  return { ...prev, ...patch, page: undefined };
}
